 
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getMileage, createMileage, deleteMileage, getEmployees, getProjects } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { formatDate } from '../../lib/workStatus';
import { Car, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const now = new Date();
const MONTHS = Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: new Date(2024, i, 1).toLocaleString('lv', { month: 'long' }) }));

const MileageManagement = () => {
  const [entries, setEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState({ employee_id: 'all', year: String(now.getFullYear()), month: String(now.getMonth() + 1) });
  const [form, setForm] = useState({ date: now.toISOString().split('T')[0], employee_id: '', kilometers: '', note: '', project_id: '' });
  const [saving, setSaving] = useState(false);

  const params = useMemo(() => {
    const p = { year: Number(filters.year), month: Number(filters.month) };
    if (filters.employee_id !== 'all') p.employee_id = filters.employee_id;
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    try {
      const [data, employeeData, projectData] = await Promise.all([getMileage(params), getEmployees(), getProjects()]);
      setEntries(data || []); setEmployees(employeeData || []); setProjects(projectData || []);
    } catch (error) {
      toast.error('Neizdevās ielādēt kilometrus');
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee_id || !form.kilometers) { toast.error('Izvēlieties darbinieku un ievadiet km'); return; }
    setSaving(true);
    try {
      await createMileage({ date: `${form.date}T00:00:00`, employee_id: form.employee_id, kilometers: Number(form.kilometers), note: form.note || null, project_id: form.project_id || null });
      toast.success('Kilometri saglabāti');
      setForm((p) => ({ ...p, kilometers: '', note: '' }));
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Neizdevās saglabāt');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Dzēst ierakstu?')) return;
    try { await deleteMileage(id); load(); } catch (error) { toast.error('Neizdevās dzēst'); }
  };

  const totalKm = entries.reduce((acc, e) => acc + Number(e.kilometers || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-mileage-page">
      <div>
        <h1 className="text-2xl font-bold text-[#1F3D2B] flex items-center gap-2" data-testid="admin-mileage-title"><Car className="w-6 h-6" /> Kilometru uzskaite</h1>
        <p className="text-[#2E2E2E]/70">Nobrauktos kilometrus ievada tikai administrators. Darbiniekiem šī sadaļa nav redzama.</p>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end" data-testid="admin-mileage-form">
            <div className="space-y-1"><Label>Datums</Label><Input type="date" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} data-testid="admin-mileage-date" /></div>
            <div className="space-y-1">
              <Label>Darbinieks</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm((p) => ({ ...p, employee_id: v }))}>
                <SelectTrigger data-testid="admin-mileage-employee"><SelectValue placeholder="Izvēlieties" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id} data-testid={`admin-mileage-employee-${e.id}`}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Objekts (peļņas aprēķinam)</Label>
              <Select value={form.project_id || 'none'} onValueChange={(v) => setForm((p) => ({ ...p, project_id: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="admin-mileage-project"><SelectValue placeholder="Nav piesaistīts" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Nav piesaistīts</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`admin-mileage-project-${p.id}`}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Nobrauktie km</Label><Input type="number" step="0.1" value={form.kilometers} onChange={(e) => setForm((p) => ({ ...p, kilometers: e.target.value }))} data-testid="admin-mileage-km" /></div>
            <div className="space-y-1"><Label>Piezīme</Label><Input value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} placeholder="Latgales objekti" data-testid="admin-mileage-note" /></div>
            <Button type="submit" disabled={saving} className="bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="admin-mileage-submit">{saving ? 'Saglabā...' : 'Pievienot'}</Button>
          </form>
        </CardContent>
      </Card>

      <div className="grid grid-cols-3 gap-3" data-testid="admin-mileage-filters">
        <Select value={filters.employee_id} onValueChange={(v) => setFilters((p) => ({ ...p, employee_id: v }))}>
          <SelectTrigger data-testid="admin-mileage-filter-employee"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Visi darbinieki</SelectItem>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.year} onValueChange={(v) => setFilters((p) => ({ ...p, year: v }))}>
          <SelectTrigger data-testid="admin-mileage-filter-year"><SelectValue /></SelectTrigger>
          <SelectContent>{[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.month} onValueChange={(v) => setFilters((p) => ({ ...p, month: v }))}>
          <SelectTrigger data-testid="admin-mileage-filter-month"><SelectValue /></SelectTrigger>
          <SelectContent>{MONTHS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <div className="px-6 py-3 border-b text-sm flex justify-between"><span>{entries.length} ieraksti</span><span data-testid="admin-mileage-total">Kopā: <strong>{totalKm.toFixed(1)} km</strong></span></div>
          {entries.length === 0 ? <div className="py-10 text-center text-[#2E2E2E]/60" data-testid="admin-mileage-empty">Nav ierakstu.</div> : (
            <table className="w-full text-sm">
              <thead className="bg-[#f8faf8] text-left"><tr><th className="px-6 py-3">Datums</th><th className="px-6 py-3">Darbinieks</th><th className="px-6 py-3">Objekts</th><th className="px-6 py-3">Nobrauktie km</th><th className="px-6 py-3">Piezīme</th><th className="px-6 py-3"></th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {entries.map((e) => (
                  <tr key={e.id} data-testid={`admin-mileage-row-${e.id}`}>
                    <td className="px-6 py-3">{formatDate(e.date)}</td><td className="px-6 py-3">{e.employee_name}</td><td className="px-6 py-3 text-[#2E2E2E]/70">{e.project_name || '-'}</td><td className="px-6 py-3 font-medium">{e.kilometers} km</td><td className="px-6 py-3 text-[#2E2E2E]/70">{e.note || '-'}</td>
                    <td className="px-6 py-3 text-right"><Button size="sm" variant="ghost" onClick={() => remove(e.id)} data-testid={`admin-mileage-delete-${e.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MileageManagement;
